import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { Loader2, Plane, Car, Building, Compass, FileText, Map as MapIcon, Image as ImageIcon, Video, CalendarDays, Sparkles, Printer, Info, ShieldAlert, ChevronDown, Paperclip } from "lucide-react";
import { youtubeId, mapsEmbedUrl, formatPrice } from "@/components/proposal/blocks";
import { Gallery } from "@/components/quote/Gallery";
import { Attachments } from "@/components/quote/Attachments";
import { LocationMap, InteractiveRouteMap } from "@/components/quote/Maps";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const ICONS = {
  intro: Sparkles, day_separator: CalendarDays, flight: Plane, transfer: Car,
  hotel: Building, activity: Compass, map: MapIcon, gallery: ImageIcon, video: Video, note: FileText,
};

const fmtDate = (d) => { try { return format(parseISO(d), "EEEE d MMMM yyyy", { locale: fr }); } catch { return d; } };

const blockImages = (b) => {
  if (b.images && b.images.length > 0) return b.images;
  return [];
};

// Simple markdown-lite for ## headings & lines
const mdRender = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((l, i) => {
    if (l.startsWith("## ")) return <h4 key={i} className="font-serif-display text-xl mt-5 mb-2">{l.slice(3)}</h4>;
    if (l.startsWith("# ")) return <h3 key={i} className="font-serif-display text-2xl mt-6 mb-2">{l.slice(2)}</h3>;
    if (l.trim() === "") return <div key={i} className="h-2" />;
    return <p key={i} className="text-sm text-[#1A1B18] leading-relaxed">{l}</p>;
  });
};

export default function PublicProposal({ source = "proposal" }) {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isCarnet = source === "carnet";

  useEffect(() => {
    const url = isCarnet ? `/public/carnets/${slug}` : `/public/proposals/${slug}`;
    api.get(url)
      .then((r) => setP(r.data))
      .catch(() => setError(isCarnet ? "Ce carnet n'existe pas ou n'est plus disponible." : "Ce devis n'existe pas ou a expiré."))
      .finally(() => setLoading(false));
  }, [slug, isCarnet]);

  // Compute totals + group blocks by category for quick nav
  const summary = useMemo(() => {
    if (!p) return { pubTotal: 0, dealTotal: 0, categories: {} };
    let pubTotal = 0, dealTotal = 0;
    const categories = {};
    (p.blocks || []).forEach((b) => {
      if (b.public_price) pubTotal += Number(b.public_price);
      if (b.deal_price) dealTotal += Number(b.deal_price);
      if (b.type !== "day_separator") {
        categories[b.type] = (categories[b.type] || 0) + 1;
      }
    });
    return { pubTotal, dealTotal, categories };
  }, [p]);

  // Group navigable blocks by type (keeps original order index), for the quick-nav dropdowns
  const blocksByType = useMemo(() => {
    const map = {};
    (p?.blocks || []).forEach((b, i) => {
      if (b.type === "day_separator") return;
      if (!map[b.type]) map[b.type] = [];
      map[b.type].push({ ...b, _idx: i });
    });
    return map;
  }, [p]);

  // Which nav dropdown is open + its anchor coords (fixed-positioned so it can never be clipped by the scroll bar)
  const [openMenu, setOpenMenu] = useState(null); // { type, x, y } | null

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (error || !p) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><p className="font-serif-display text-2xl">{error || "Devis introuvable."}</p></div>;

  const totalPub = p.total_public_price ?? summary.pubTotal;
  const totalDeal = p.total_deal_price ?? summary.dealTotal;
  const hasDiscount = totalPub && totalDeal && Number(totalPub) > Number(totalDeal);
  const savings = hasDiscount ? Number(totalPub) - Number(totalDeal) : null;

  const navItems = [
    { type: "flight", label: "Vols", icon: Plane },
    { type: "hotel", label: "Hôtels", icon: Building },
    { type: "transfer", label: "Transferts", icon: Car },
    { type: "activity", label: "Activités", icon: Compass },
    { type: "video", label: "Vidéos", icon: Video },
  ].filter((n) => (blocksByType[n.type]?.length || 0) > 0);

  const scrollToBlockId = (id) => {
    const el = document.querySelector(`[data-block-id="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Toggle a category dropdown, anchoring it to the clicked button via fixed coords
  const toggleMenu = (type, e) => {
    if (openMenu?.type === type) { setOpenMenu(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setOpenMenu({ type, x: r.left, y: r.bottom + 6 });
  };

  const labelFor = (n, item, i) => item.title || item.subtitle || `${n.label.replace(/s$/, "")} ${i + 1}`;
  const openItems = openMenu ? (blocksByType[openMenu.type] || []) : [];
  const openDef = openMenu ? navItems.find((n) => n.type === openMenu.type) : null;

  const PriceCard = ({ compact = false }) => (
    <div className="bg-[#1A1B18] text-white p-6 md:p-7" data-testid="price-card">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#E4DCCF]">{isCarnet ? "Ce voyage, à partir de" : "Votre tarif Deal Travel"}</p>
      {hasDiscount && !isCarnet && (
        <p className="mt-3 text-white/45 line-through text-lg">{formatPrice(totalPub, p.currency)}</p>
      )}
      <p className="font-serif-display text-4xl md:text-5xl mt-1 leading-none">
        {isCarnet && <span className="text-lg align-middle mr-1 text-white/70">dès</span>}
        {formatPrice(totalDeal || totalPub || 0, p.currency)}
      </p>
      {savings && !isCarnet ? (
        <p className="mt-4 inline-block bg-[#E4DCCF] text-[#1A1B18] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em]">
          Vous économisez {formatPrice(savings, p.currency)}
        </p>
      ) : null}
      {!compact && (
        <>
          {isCarnet ? (
            <>
              <a
                href={`/?dest=${encodeURIComponent(p.destination || p.title || "")}#devis`}
                className="mt-6 block w-full text-center bg-[#E4DCCF] hover:bg-white text-[#1A1B18] py-3 text-[10px] uppercase tracking-[0.22em] transition-colors print:hidden"
                data-testid="carnet-create-cta"
              >
                Créer mon voyage sur-mesure
              </a>
              <p className="mt-4 text-[11px] text-white/55 leading-relaxed">
                Tarif indicatif à partir de, pour un voyage inspiré de ce carnet. Chaque voyage est composé sur-mesure selon vos envies et votre budget.
              </p>
            </>
          ) : (
            <>
              <a
                href="mailto:contact@dealtravel.eu"
                className="mt-6 block w-full text-center bg-[#E4DCCF] hover:bg-white text-[#1A1B18] py-3 text-[10px] uppercase tracking-[0.22em] transition-colors print:hidden"
                data-testid="price-card-cta"
              >
                Réserver ce voyage
              </a>
              <p className="mt-4 text-[11px] text-white/55 leading-relaxed">
                Tarif négocié, sous réserve de disponibilité au moment de la confirmation.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );


  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1B18]">
      {isCarnet ? (
        <Helmet>
          <title>{`${p.title || "Carnet de voyage"} | Carnet de Voyage DealTravel`}</title>
          <meta name="description" content={(p.public_intro || p.intro || p.subtitle || `Découvrez un véritable carnet de voyage ${p.destination ? "— " + p.destination : ""} conçu sur-mesure par DealTravel.`).slice(0, 300)} />
          <link rel="canonical" href={`https://dealtravel.eu/carnets/${slug}`} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={`${p.title || "Carnet de voyage"} | DealTravel`} />
          <meta property="og:description" content={(p.public_intro || p.subtitle || "Un carnet de voyage sur-mesure DealTravel.").slice(0, 300)} />
          {p.cover_image && <meta property="og:image" content={p.cover_image} />}
          <meta property="og:locale" content="fr_FR" />
        </Helmet>
      ) : (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      <button
        onClick={() => window.print()}
        className="fixed top-6 right-6 z-50 bg-white border border-[#EAE8E3] hover:border-[#1A1B18] px-4 py-2 text-[10px] uppercase tracking-[0.22em] flex items-center gap-2 print:hidden"
        data-testid="btn-print"
      >
        <Printer className="h-3.5 w-3.5" /> Télécharger PDF
      </button>

      {/* HERO — bannière de présentation immersive (image en arrière-plan ou repli sombre) */}
      <section className={`relative flex items-end overflow-hidden bg-[#1A1B18] ${p.cover_image ? "min-h-[68vh] md:min-h-[82vh]" : "min-h-[42vh] md:min-h-[48vh]"}`}>
        {p.cover_image ? (
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${p.cover_image})` }} aria-hidden />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#2a2b27] via-[#1A1B18] to-[#3a2a22]" aria-hidden />
        )}
        {/* Voile de lisibilité — plus dense en bas, là où se trouve le texte */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-14 md:pb-20 pt-32 text-white">
          <p className="label-eyebrow text-[#E4DCCF]">Deal Travel · Présentation du voyage</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl leading-[1.04] mt-5 md:mt-6 max-w-[20ch]">{p.title}</h1>
          {p.subtitle && (
            <p className="mt-4 md:mt-5 font-serif-display text-xl md:text-2xl lg:text-3xl text-white/90 leading-snug max-w-[36ch]">{p.subtitle}</p>
          )}
          {(p.destination || p.travelers) && (
            <p className="mt-4 text-sm md:text-base text-white/80">
              {p.departure_city && `${p.departure_city} → `}{p.destination}{p.destination && p.travelers ? " · " : ""}{p.travelers}
            </p>
          )}
          {(p.departure_date || p.return_date) && (
            <p className="mt-2 text-xs md:text-sm text-white/70 uppercase tracking-[0.22em]">
              {p.departure_date && format(parseISO(p.departure_date), "d MMM yyyy", { locale: fr })}
              {p.departure_date && p.return_date && " → "}
              {p.return_date && format(parseISO(p.return_date), "d MMM yyyy", { locale: fr })}
            </p>
          )}
          {p.client_name && (
            <p className="mt-8 md:mt-10 text-sm text-white/70">Préparé pour <span className="text-white">{p.client_name}</span></p>
          )}
        </div>
      </section>

      {/* Quick navigation bar */}
      {navItems.length > 0 && (
        <nav className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-[#EAE8E3] print:hidden">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-3 flex items-center gap-2 overflow-x-auto">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#96836B] shrink-0 mr-2">Navigation</span>
            {navItems.map((n) => {
              const Icon = n.icon;
              const items = blocksByType[n.type] || [];
              // Multiple elements → dropdown (fixed-positioned, rendered below). Single → direct scroll.
              if (items.length > 1) {
                const isOpen = openMenu?.type === n.type;
                return (
                  <button
                    key={n.type}
                    onClick={(e) => toggleMenu(n.type, e)}
                    data-testid={`nav-menu-${n.type}`}
                    aria-expanded={isOpen}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border whitespace-nowrap transition-colors ${isOpen ? "bg-[#1A1B18] text-white border-[#1A1B18]" : "border-transparent hover:bg-[#FAF9F6] hover:border-[#EAE8E3]"}`}
                  >
                    <Icon className={`h-3.5 w-3.5 ${isOpen ? "text-[#E4DCCF]" : "text-[#96836B]"}`} /> {n.label} ({items.length})
                    <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                );
              }
              return (
                <button key={n.type} onClick={() => scrollToBlockId(items[0]?.id)} data-testid={`nav-link-${n.type}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs hover:bg-[#FAF9F6] border border-transparent hover:border-[#EAE8E3] whitespace-nowrap transition-colors">
                  <Icon className="h-3.5 w-3.5 text-[#96836B]" /> {n.label}
                </button>
              );
            })}
            {p.cancellation_policy && (
              <button onClick={() => document.getElementById("annulation")?.scrollIntoView({ behavior: "smooth" })} className="flex items-center gap-1.5 px-3 py-1.5 text-xs hover:bg-[#FAF9F6] border border-transparent hover:border-[#EAE8E3] whitespace-nowrap transition-colors">
                <ShieldAlert className="h-3.5 w-3.5 text-[#96836B]" /> Annulation
              </button>
            )}
            {p.practical_info && (
              <button onClick={() => document.getElementById("infos-pratiques")?.scrollIntoView({ behavior: "smooth" })} className="flex items-center gap-1.5 px-3 py-1.5 text-xs hover:bg-[#FAF9F6] border border-transparent hover:border-[#EAE8E3] whitespace-nowrap transition-colors">
                <Info className="h-3.5 w-3.5 text-[#96836B]" /> Infos pratiques
              </button>
            )}
          </div>
        </nav>
      )}

      {/* Fixed-position dropdown panel — rendered outside the overflow-x-auto bar so it is never clipped */}
      {openMenu && openDef && (
        <>
          {/* Click-away overlay */}
          <div className="fixed inset-0 z-40 print:hidden" onClick={() => setOpenMenu(null)} aria-hidden />
          <div
            className="fixed z-50 bg-white border border-[#EAE8E3] shadow-2xl min-w-[260px] max-w-[340px] max-h-[70vh] overflow-y-auto print:hidden animate-in fade-in slide-in-from-top-1 duration-150"
            style={{ left: Math.min(openMenu.x, (typeof window !== "undefined" ? window.innerWidth : 1024) - 360), top: openMenu.y }}
            data-testid={`nav-dropdown-${openMenu.type}`}
            role="menu"
          >
            <p className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-[0.22em] text-[#96836B] border-b border-[#F2EFE9] flex items-center gap-2">
              <openDef.icon className="h-3.5 w-3.5" /> {openDef.label}
            </p>
            {openItems.map((item, i) => (
              <button
                key={item.id}
                onClick={() => { setOpenMenu(null); scrollToBlockId(item.id); }}
                data-testid={`nav-item-${openMenu.type}-${i}`}
                className="block w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAF9F6] transition-colors border-b border-[#F2EFE9] last:border-b-0"
                role="menuitem"
              >
                {labelFor(openDef, item, i)}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-8 py-12">
        {/* Mobile-only price card at the very top */}
        <div className="lg:hidden -mt-2 -mb-4">
          <PriceCard compact />
        </div>

        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* INTRO */}
          {p.intro && (
            <section className="bg-white border border-[#EAE8E3] p-8 md:p-10">
              <p className="label-eyebrow">Votre voyage</p>
              <p className="font-serif-display text-2xl md:text-3xl mt-6 leading-relaxed whitespace-pre-line">{p.intro}</p>
            </section>
          )}

          {/* BLOCKS */}
          {(p.blocks || []).map((b, i) => <BlockView key={b.id || i} block={b} currency={p.currency} />)}

          {/* CANCELLATION */}
          {p.cancellation_policy && (
            <section id="annulation" className="bg-white border border-[#EAE8E3] p-8 md:p-10 scroll-mt-20">
              <p className="label-eyebrow">Politique d&apos;annulation</p>
              <h2 className="font-serif-display text-3xl mt-4">Annulation &amp; modification</h2>
              <div className="mt-6">{mdRender(p.cancellation_policy)}</div>
            </section>
          )}

          {/* PRACTICAL INFO */}
          {p.practical_info && (
            <section id="infos-pratiques" className="bg-white border border-[#EAE8E3] p-8 md:p-10 scroll-mt-20">
              <p className="label-eyebrow">Informations pratiques</p>
              <h2 className="font-serif-display text-3xl mt-4">Préparer votre voyage</h2>
              <div className="mt-6">{mdRender(p.practical_info)}</div>
            </section>
          )}

          {/* CONDITIONS */}
          {p.conditions && (
            <section className="bg-white border border-[#EAE8E3] p-8 md:p-10">
              <p className="label-eyebrow">Conditions générales</p>
              <div className="mt-6 text-sm text-[#1A1B18] leading-relaxed whitespace-pre-line">{p.conditions}</div>
            </section>
          )}
        </div>

        {/* Sticky pricing sidebar (desktop only) */}
        <aside className="hidden lg:block lg:col-span-1">
          <div className="sticky top-20 space-y-4">
            <PriceCard />
          </div>
        </aside>
      </div>

      {/* FOOTER */}
      <footer className="py-12 px-6 md:px-12 text-center text-xs text-[#737373] border-t border-[#EAE8E3]">
        <p className="font-serif-display text-2xl text-[#1A1B18]">Deal Travel</p>
        <p className="mt-2 tracking-[0.18em] uppercase">Voyages d&apos;exception · Sur-mesure</p>
        <p className="mt-3">contact@dealtravel.eu</p>
      </footer>
    </div>
  );
}

const BlockView = ({ block, currency }) => {
  const Icon = ICONS[block.type] || FileText;
  const hasPriceDiscount = block.public_price && block.deal_price && Number(block.public_price) > Number(block.deal_price);
  const images = blockImages(block);

  if (block.type === "day_separator") {
    return (
      <div className="relative pt-12 scroll-mt-20" data-block-type={block.type}>
        <div className="absolute left-0 right-0 top-1/2 h-px bg-[#E4DCCF]" />
        <div className="relative inline-flex items-center gap-3 bg-[#FAF9F6] pr-6">
          <CalendarDays className="h-5 w-5 text-[#96836B]" strokeWidth={1.4} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">{block.title || "Étape"}</p>
            {block.date && <p className="font-serif-display text-2xl">{fmtDate(block.date)}</p>}
          </div>
        </div>
      </div>
    );
  }

  if (block.type === "video") {
    const id = youtubeId(block.video_url);
    return (
      <article className="bg-white border border-[#EAE8E3] overflow-hidden scroll-mt-20" data-block-type={block.type} data-block-id={block.id}>
        <Header icon={Icon} block={block} />
        {id && (
          <div className="aspect-video">
            <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${id}`} title={block.title} allowFullScreen frameBorder="0" />
          </div>
        )}
      </article>
    );
  }

  if (block.type === "map") {
    const url = mapsEmbedUrl(block.map_query || block.location || block.address);
    return (
      <article className="bg-white border border-[#EAE8E3] overflow-hidden scroll-mt-20" data-block-type={block.type} data-block-id={block.id}>
        <Header icon={Icon} block={block} />
        {url && (
          <div className="h-[420px] w-full">
            <iframe className="w-full h-full" src={url} title="Carte" loading="lazy" frameBorder="0" />
          </div>
        )}
      </article>
    );
  }

  return (
    <article className="bg-white border border-[#EAE8E3] overflow-hidden scroll-mt-20" data-block-type={block.type} data-block-id={block.id}>
      {images.length > 0 && (
        <div className="w-full">
          <Gallery images={images} alt={block.title || ""} aspect="aspect-[16/9]" />
        </div>
      )}
      <div className="p-6 md:p-8">
        <Header icon={Icon} block={block} />

        {(block.type === "flight" || block.type === "transfer") && (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-[#EAE8E3] pt-5">
            {block.departure && <Stat k="Départ" v={`${block.departure}${block.time_start ? ` · ${block.time_start}` : ""}`} />}
            {block.arrival && <Stat k="Arrivée" v={`${block.arrival}${block.time_end ? ` · ${block.time_end}` : ""}`} />}
            {block.duration && <Stat k="Durée" v={block.duration} />}
            {block.carrier && <Stat k="Compagnie" v={block.carrier} />}
            {block.reference && <Stat k="Référence" v={block.reference} />}
          </div>
        )}

        {block.type === "flight" && (
          <div className="mt-6 border border-[#EAE8E3] overflow-hidden">
            <InteractiveRouteMap mode="flight" departure={block.departure_place} arrival={block.arrival_place} depText={block.departure} arrText={block.arrival} />
          </div>
        )}
        {block.type === "transfer" && (
          <div className="mt-6 border border-[#EAE8E3] overflow-hidden">
            <InteractiveRouteMap mode="transfer" departure={block.departure_place} arrival={block.arrival_place} depText={block.departure} arrText={block.arrival} />
          </div>
        )}

        {block.type === "hotel" && (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-[#EAE8E3] pt-5">
            {block.check_in && <Stat k="Check-in" v={fmtDate(block.check_in)} />}
            {block.check_out && <Stat k="Check-out" v={fmtDate(block.check_out)} />}
            {block.nights && <Stat k="Nuits" v={`${block.nights}`} />}
            {block.address && <Stat k="Adresse" v={block.address} />}
            {block.rating && <Stat k="Catégorie" v={`${block.rating} ★`} />}
            {block.included && block.included.length > 0 && (
              <div className="md:col-span-3">
                <p className="label-eyebrow">Inclus</p>
                <ul className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1 text-sm">
                  {block.included.map((i) => <li key={i} className="text-[#1A1B18]">— {i}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {block.description && (
          <p className="mt-5 text-sm leading-relaxed text-[#1A1B18] whitespace-pre-line">{block.description}</p>
        )}

        {(block.type === "hotel" || block.type === "activity") && (block.place || block.map_query || block.address || block.location) && (
          <div className="mt-6 h-[300px] border border-[#EAE8E3] overflow-hidden">
            <LocationMap place={block.place} text={block.map_query || block.address || block.location} />
          </div>
        )}

        {(block.public_price || block.deal_price) && (
          <div className="mt-6 pt-5 border-t border-[#EAE8E3] flex items-baseline gap-4 flex-wrap">
            {hasPriceDiscount && (
              <span className="text-[#737373] line-through text-base">{formatPrice(block.public_price, currency)}</span>
            )}
            <span className="font-serif-display text-2xl">{formatPrice(block.deal_price || block.public_price, currency)}</span>
            {block.price_label && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">{block.price_label}</span>
            )}
          </div>
        )}

        {(block.attachments || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-dashed border-[#EAE8E3]">
            <p className="label-eyebrow mb-2.5 flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Documents</p>
            <Attachments attachments={block.attachments} />
          </div>
        )}
      </div>
    </article>
  );
};

const Header = ({ icon: Icon, block }) => (
  <header className="flex items-start gap-4">
    <span className="w-10 h-10 rounded-full bg-[#FAF9F6] border border-[#EAE8E3] flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-[#96836B]" strokeWidth={1.5} />
    </span>
    <div className="flex-1 min-w-0">
      {block.subtitle && <p className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">{block.subtitle}</p>}
      <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mt-1">{block.title || "Étape"}</h3>
    </div>
  </header>
);

const Stat = ({ k, v }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">{k}</p>
    <p className="text-sm text-[#1A1B18] mt-1">{v}</p>
  </div>
);

